import React, { useEffect, useRef, useState } from 'react';
import {
  Paperclip, ArrowUp, AlertTriangle, HelpCircle, Ban, FileSpreadsheet,
  FlaskConical, X, ChevronDown, ChevronRight,
} from 'lucide-react';
import { operatorService, OperatorPlan } from 'api/services/operator.service';
import {
  availableBrowserModels, browserModelsPossible, loadBrowserModel, downloadedModels, supportsTools,
  BrowserModelHandle, SuppliedReading, LoadProgress,
} from './browserModel';

/**
 * Ask — a conversation, not a form.
 *
 * <h3>Why it was rebuilt</h3>
 * The first version was a form: a box, a button, and every answer replacing the
 * last one in a stack of panels. That is fine for a thing you do once and wrong
 * for a thing you do five times in a row, which is what this turned out to be —
 * you ask, you read, you rephrase. A form throws away the rephrasing; a
 * transcript keeps it beside the answer, which is the only way to see that two
 * wordings of the same question got different readings.
 *
 * <h3>What is kept from the form</h3>
 * Everything the plan says. It is laid out tighter inside a turn, but no count,
 * blocker or warning was dropped to make it fit: the reason this screen is
 * trusted is that it shows what it would skip as prominently as what it would
 * do.
 */

const short = (id: string) => id.replace(/-q[0-9]f.*$/i, '').replace(/-MLC$/i, '');

interface Turn {
  id: number;
  you: string;
  fileName?: string;
  pending: boolean;
  plan?: OperatorPlan;
  reading?: SuppliedReading;
  error?: string;
}

const SUGGESTIONS = [
  'what does standing mean',
  'show my campaigns',
  'what happens when someone replies STOP',
  'how do I add people to a campaign',
];

const OperatorConsole: React.FC = () => {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [message, setMessage] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const bottom = useRef<HTMLDivElement>(null);

  // ── the interpreter ─────────────────────────────────────────────────────
  const [showPicker, setShowPicker] = useState(false);
  const [models, setModels] = useState<string[]>([]);
  const [modelId, setModelId] = useState('');
  const [engine, setEngine] = useState<BrowserModelHandle | null>(null);
  const [progress, setProgress] = useState<LoadProgress | null>(null);
  const [loadNote, setLoadNote] = useState<string | null>(null);
  const [downloaded, setDownloaded] = useState<string[]>(() => downloadedModels());
  const canRunLocally = browserModelsPossible();

  // block:'nearest' rather than the default 'start': the default drags the
  // whole page so the last turn sits under the sticky header, which reads as
  // the page jumping away from what you just asked.
  useEffect(() => {
    if (turns.length) bottom.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [turns]);

  const openPicker = async () => {
    setShowPicker(true);
    if (!canRunLocally || models.length) return;
    try { setModels(await availableBrowserModels()); }
    catch (e: any) { setLoadNote(`Could not read the model catalogue: ${e?.message || e}`); }
  };

  const load = async (id: string) => {
    if (engine) { await engine.unload(); setEngine(null); }
    setModelId(id); setLoadNote(null);
    setProgress({ fraction: 0, text: downloaded.includes(id) ? 'reading from this browser…' : 'starting…' });
    try {
      const h = await loadBrowserModel(id, setProgress);
      setEngine(h); setProgress(null); setDownloaded(downloadedModels());
      setLoadNote(null);
    } catch (e: any) {
      setProgress(null);
      setLoadNote(`Could not load ${short(id)}: ${e?.message || e}`);
    }
  };

  const useRules = async () => {
    if (engine) await engine.unload();
    setEngine(null); setModelId(''); setProgress(null); setLoadNote(null);
  };

  const send = async (text?: string) => {
    const said = (text ?? message).trim();
    if (!said || busy) return;
    const id = Date.now();
    const attached = file;
    setTurns((t) => [...t, { id, you: said, fileName: attached?.name, pending: true }]);
    setMessage(''); setFile(null);
    if (fileInput.current) fileInput.current.value = '';
    setBusy(true);

    const settle = (patch: Partial<Turn>) =>
      setTurns((t) => t.map((x) => (x.id === id ? { ...x, ...patch, pending: false } : x)));

    let supplied: { intent: string; campaignHint: string | null } | undefined;
    let reading: SuppliedReading | undefined;
    if (engine) {
      try {
        reading = await engine.ask(said, !!attached, []);
        supplied = { intent: reading.intent, campaignHint: reading.campaignHint };
      } catch (e: any) {
        settle({ error: `${short(modelId)} could not read that: ${e?.message || e}` });
        setBusy(false);
        return;
      }
    }
    const res = await operatorService.plan(said, attached || undefined, supplied);
    setBusy(false);
    if (!res.success || !res.data) { settle({ reading, error: res.error || 'Could not answer that' }); return; }
    settle({ reading, plan: res.data });
  };

  return (
    // No height of our own, and no scroller of our own.
    //
    // This was h-[calc(100vh-4rem)] with an overflow-y-auto middle, which
    // guessed the height of chrome it cannot see and nested a second scroll
    // area inside the Layout's own <main className="flex-1 overflow-y-auto">.
    // Two scrollbars, and the header scrolled away with the page — so the
    // interpreter you are using disappeared the moment the conversation got
    // long enough to matter.
    //
    // Instead: one scroll container, the Layout's, with the header and the
    // composer stuck to its edges. The negative margins pull them out to the
    // full width of the padded wrapper so nothing shows in the gutter behind
    // them as content passes underneath.
    <div className="-m-4 lg:-m-6 flex flex-col min-h-[calc(100vh-8rem)]">
      {/* ── who is reading ───────────────────────────────────────────────── */}
      <header
        className="sticky top-0 z-20 flex items-center gap-3 px-4 sm:px-6 py-3 border-b border-gray-200 dark:border-gray-700 backdrop-blur"
        style={{ background: 'var(--canvas)' }}
      >
        <h1 className="text-base font-semibold text-gray-900 dark:text-white">Ask</h1>
        <span className="text-xs text-gray-400 hidden sm:inline">nothing here sends a message</span>
        <button
          type="button"
          onClick={() => (showPicker ? setShowPicker(false) : openPicker())}
          className="ml-auto flex items-center gap-1.5 px-2 py-1 rounded text-xs border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          title="Which interpreter reads your sentence"
        >
          <FlaskConical className="w-3.5 h-3.5" />
          {engine ? short(engine.id) : 'cpass rules'}
          {showPicker ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        </button>
      </header>

      {showPicker && (
        <section className="sticky top-[3.25rem] z-10 px-4 sm:px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 space-y-2">
          {!canRunLocally ? (
            <p className="text-xs text-gray-500">
              This browser has no WebGPU, so a local model cannot run here — Chrome or Edge on a
              desktop can. cpass's own rules are reading your sentences.
            </p>
          ) : (
            <>
              <ul className="divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 max-h-56 overflow-y-auto">
                <li className="flex items-center gap-2 px-2 py-1.5">
                  <span className="text-xs flex-1">cpass rules <span className="text-gray-400">· instant, no download</span></span>
                  {!engine ? <Badge tone="live">in use</Badge>
                    : <button type="button" onClick={useRules} className="text-xs text-blue-600 hover:underline">use this</button>}
                </li>
                {models.map((m) => {
                  const live = engine?.id === m;
                  const have = downloaded.includes(m);
                  const loading = progress != null && modelId === m;
                  return (
                    <li key={m} className="flex items-center gap-2 px-2 py-1.5">
                      <span className="text-xs flex-1 truncate" title={m}>
                        {short(m)}{' '}
                        {/* Tool calling is only in the 7-8B Hermes models, so the
                            badge and the size warning belong together: it is the
                            better contract AND the one that costs 4-5 GB. */}
                        {supportsTools(m) && <Badge tone="tool">tools</Badge>}{' '}
                        <span className="text-gray-400">
                          · {have ? 'already on this device' : supportsTools(m) ? 'not downloaded — about 4–5 GB' : 'not downloaded yet'}
                        </span>
                      </span>
                      {live ? <Badge tone="live">in use</Badge>
                        : loading ? <Badge tone="busy">loading</Badge>
                        : <button type="button" disabled={progress != null} onClick={() => load(m)}
                                  className="text-xs text-blue-600 hover:underline disabled:text-gray-400 disabled:no-underline">
                            {have ? 'use this' : 'download & use'}
                          </button>}
                    </li>
                  );
                })}
              </ul>
              {progress && (
                <div className="space-y-1">
                  <div className="h-1.5 rounded bg-gray-200 dark:bg-gray-700 overflow-hidden">
                    <div className="h-full bg-blue-600 transition-all"
                         style={{ width: `${Math.round((progress.fraction ?? 0) * 100)}%` }} />
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-300 font-mono truncate" title={progress.text}>
                    {progress.fraction != null && `${Math.round(progress.fraction * 100)}% · `}{progress.text}
                  </p>
                </div>
              )}
              {loadNote && <p className="text-xs text-red-700 dark:text-red-300">{loadNote}</p>}
              <p className="text-xs text-gray-500">
                A model runs on this device and reads your sentence only — the campaign, every count
                and the cost still come from the server. Models marked <b>tools</b> choose a named
                function and fill its argument, which is the contract the MCP tools will use; only
                the 7–8B Hermes models can do it, and they are a 4–5 GB download needing a GPU with
                room for them. The rest answer a JSON shape instead.
              </p>
            </>
          )}
        </section>
      )}

      {/* ── the conversation ─────────────────────────────────────────────── */}
      <div className="flex-1 px-4 sm:px-6 py-4">
        <div className="max-w-3xl mx-auto space-y-5">
          {turns.length === 0 && (
            <div className="pt-6 space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Ask about your campaigns, or describe something you want to do and see exactly what
                would happen first.
              </p>
              <div className="flex flex-wrap gap-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} type="button" onClick={() => send(s)}
                          className="px-3 py-1.5 text-xs rounded-full border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {turns.map((t) => (
            <div key={t.id} className="space-y-3">
              <div className="flex justify-end">
                <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-blue-600 text-white px-3.5 py-2 text-sm">
                  {t.you}
                  {t.fileName && (
                    <div className="text-xs text-blue-100 mt-1 flex items-center gap-1">
                      <Paperclip className="w-3 h-3" /> {t.fileName}
                    </div>
                  )}
                </div>
              </div>
              <div className="max-w-[95%]">
                {t.pending ? <p className="text-sm text-gray-400">reading…</p>
                  : t.error ? <p className="text-sm text-red-700 dark:text-red-300">{t.error}</p>
                  : t.plan ? <Answer plan={t.plan} /> : null}
                {t.reading && <Reading reading={t.reading} modelId={modelId} />}
              </div>
            </div>
          ))}
          <div ref={bottom} />
        </div>
      </div>

      {/* ── the composer ─────────────────────────────────────────────────── */}
      <div
        className="sticky bottom-16 lg:bottom-0 z-20 border-t border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-3 backdrop-blur"
        style={{ background: 'var(--canvas)' }}
      >
        <div className="max-w-3xl mx-auto">
          {file && (
            <div className="mb-2 inline-flex items-center gap-2 px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-xs">
              <FileSpreadsheet className="w-3.5 h-3.5" /> {file.name}
              <button type="button" onClick={() => { setFile(null); if (fileInput.current) fileInput.current.value = ''; }}>
                <X className="w-3.5 h-3.5 text-gray-500 hover:text-gray-700" />
              </button>
            </div>
          )}
          <div className="flex items-end gap-2 rounded-2xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1.5">
            <label className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer shrink-0" title="Attach a spreadsheet">
              <Paperclip className="w-4 h-4 text-gray-500" />
              <input ref={fileInput} id="operator-file" type="file" className="hidden"
                     accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                     onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
            <textarea
              id="operator-message"
              rows={1}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              // Enter sends and shift+Enter breaks the line, as every chat box
              // does. Getting this backwards is the single most annoying thing a
              // text box can do.
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask about a campaign, or describe what you want to do…"
              className="flex-1 resize-none bg-transparent text-sm py-1.5 max-h-40 outline-none text-gray-900 dark:text-gray-100"
            />
            <button type="button" onClick={() => send()} disabled={busy || !message.trim()}
                    className="p-1.5 rounded-lg bg-blue-600 text-white disabled:opacity-40 shrink-0">
              <ArrowUp className="w-4 h-4" />
            </button>
          </div>
          <p className="text-[11px] text-gray-400 mt-1.5">
            Enter to send · Shift + Enter for a new line · it explains, it never sends
          </p>
        </div>
      </div>
    </div>
  );
};

/** What the model made of the sentence, under the answer it produced. */
const Reading: React.FC<{ reading: SuppliedReading; modelId: string }> = ({ reading, modelId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2 text-[11px]">
      <button type="button" onClick={() => setOpen(!open)} className="text-gray-400 hover:text-gray-600">
        {short(modelId)} read this as <b>{reading.intent}</b> in {reading.ms} ms
        {reading.mode === 'tools' && ' · called a tool'}
        {reading.mode === 'unconstrained' && ' · unconstrained'}
        {reading.discardedHint && ' · invented a campaign'}
      </button>
      {open && (
        <div className="mt-1 p-2 rounded bg-gray-900 text-gray-100 font-mono space-y-0.5 break-all">
          <div>intent: {reading.intent}</div>
          <div>campaignHint: {reading.campaignHint ?? 'none'}</div>
          {reading.discardedHint && (
            /* Reported rather than hidden: a harness that silently cleans up
               after a model cannot tell you which models need cleaning up after. */
            <div className="text-amber-300">
              discarded "{reading.discardedHint}" — those words are not in your message
            </div>
          )}
          <div className="text-gray-400">
            asked by: {reading.mode === 'tools' ? "the library's tools API — it chose a named function"
              : reading.mode === 'grammar' ? 'a grammar over the same four tools — same contract, no tools API'
              : 'nothing — this model has no working grammar backend here'}
          </div>
          <div className="text-gray-400">raw: {reading.raw}</div>
        </div>
      )}
    </div>
  );
};

const Answer: React.FC<{ plan: OperatorPlan }> = ({ plan }) => {
  const a = plan.audience;
  const m = plan.mapping;
  return (
    <div className="space-y-3 text-sm">
      <p className="text-gray-900 dark:text-gray-100 whitespace-pre-line leading-relaxed">
        {plan.answer ?? plan.summary}
      </p>

      {plan.facts?.length > 0 && (
        <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-1 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
          {plan.facts.map((f, i) => (
            <div key={i} className="flex justify-between gap-3">
              <dt className="text-gray-500 truncate" title={f.label}>{f.label}</dt>
              <dd className="font-medium tabular-nums shrink-0">{f.value}</dd>
            </div>
          ))}
        </dl>
      )}

      {plan.blockers?.length > 0 && (
        <Note tone="stop" icon={<Ban className="w-4 h-4" />} title="This cannot run as it stands"
              items={plan.blockers} />
      )}

      {a && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <Count label="Rows in the file" value={a.rowsInFile} />
          <Count label="Would be added" value={a.wouldBeAdded} strong />
          <Count label="Already queued" value={a.alreadyPending} />
          <Count label="No usable number" value={a.rowsUnusable} />
          <Count label="Do-not-contact" value={a.suppressedOrgWideDoNotContact}
                 note={a.suppressionFullyChecked ? undefined : 'a floor — partial check'} />
          <Count label="Distinct numbers" value={a.distinctNumbers} />
        </div>
      )}

      {plan.questions?.length > 0 && (
        <Note tone="ask" icon={<HelpCircle className="w-4 h-4" />} title="Worth answering first"
              items={plan.questions} />
      )}
      {plan.irreversible?.length > 0 && (
        <Note tone="warn" icon={<AlertTriangle className="w-4 h-4" />} title="What could not be taken back"
              items={plan.irreversible} />
      )}

      {(a || plan.cost.available) && (
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500">
          <span>
            {plan.cost.available
              ? <>Cost: <b className="text-gray-800 dark:text-gray-100">{plan.cost.currency} {plan.cost.total}</b> for {plan.cost.messages}</>
              /* Never a zero: "we cannot say" and "it is free" are different. */
              : <>Cost cannot be shown — {plan.cost.note}</>}
          </span>
          {plan.confirmRoles?.length > 0 && (
            <span>Confirmed by: {plan.confirmRoles.slice(0, 3).map((r) => r.replace(/_/g, ' ').toLowerCase()).join(', ')}
              {plan.confirmRoles.length > 3 && ` +${plan.confirmRoles.length - 3}`}</span>
          )}
        </div>
      )}

      {m && m.headers?.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-gray-500 hover:text-gray-700">How the columns were read</summary>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {m.headers.map((h, i) => {
              const slot = Object.entries(m.slotColumns).find(([, col]) => col === i)?.[0];
              const role = i === m.phoneColumn ? 'their number'
                : i === m.nameColumn ? 'their name'
                : slot ? `fills {{${slot}}}`
                : m.contactColumns.includes(i) ? 'kept about them' : null;
              return (
                <span key={i} className={`px-2 py-0.5 rounded border ${role
                  ? 'border-green-300 bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-200'
                  : 'border-gray-200 text-gray-400 dark:border-gray-600'}`}>
                  {h || `Column ${i + 1}`}{role ? ` → ${role}` : ' → ignored'}
                </span>
              );
            })}
          </div>
        </details>
      )}

      {a && plan.planHash && (
        <p className="text-xs text-gray-400 border-t border-gray-100 dark:border-gray-700 pt-2">
          To actually add these people, use <b>Add a whole file</b> on the campaign — same mapping,
          and it asks you to press the button.
        </p>
      )}
    </div>
  );
};

const Note: React.FC<{ tone: 'stop' | 'warn' | 'ask'; icon: React.ReactNode; title: string; items: string[] }> =
  ({ tone, icon, title, items }) => {
  const skin = tone === 'stop'
    ? 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-200'
    : tone === 'warn'
    ? 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200'
    : 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-200';
  return (
    <div className={`border rounded-lg p-3 space-y-1 ${skin}`}>
      <div className="flex items-center gap-2 text-xs font-semibold">{icon}{title}</div>
      <ul className="list-disc pl-5 space-y-1 text-xs">
        {items.map((x, i) => <li key={i}>{x}</li>)}
      </ul>
    </div>
  );
};

const Count: React.FC<{ label: string; value: number; strong?: boolean; note?: string }> =
  ({ label, value, strong, note }) => (
  <div className={`rounded-lg p-2.5 border ${strong
    ? 'border-blue-300 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700'
    : 'border-gray-200 dark:border-gray-700'}`}>
    <div className={`text-lg tabular-nums ${strong
      ? 'font-bold text-blue-800 dark:text-blue-200' : 'font-semibold text-gray-900 dark:text-gray-100'}`}>
      {value.toLocaleString()}
    </div>
    <div className="text-[11px] text-gray-500">{label}</div>
    {note && <div className="text-[11px] text-amber-700 dark:text-amber-300">{note}</div>}
  </div>
);

const Badge: React.FC<{ tone: 'live' | 'busy' | 'tool'; children: React.ReactNode }> = ({ tone, children }) => (
  <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${
    tone === 'live' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
    : tone === 'tool' ? 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-200'
    : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200'}`}>
    {children}
  </span>
);

export default OperatorConsole;
